import CalendarTodayTwoToneIcon from "@mui/icons-material/CalendarTodayTwoTone";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import DescriptionTwoToneIcon from "@mui/icons-material/DescriptionTwoTone";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import EmailTwoToneIcon from "@mui/icons-material/EmailTwoTone";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LanguageTwoToneIcon from "@mui/icons-material/LanguageTwoTone";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LocalOfferTwoToneIcon from "@mui/icons-material/LocalOfferTwoTone";
import MiscellaneousServicesTwoToneIcon from "@mui/icons-material/MiscellaneousServicesTwoTone";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import QrCodeScannerTwoToneIcon from "@mui/icons-material/QrCodeScannerTwoTone";
import StorefrontTwoToneIcon from "@mui/icons-material/StorefrontTwoTone";
import TwitterIcon from "@mui/icons-material/Twitter";
import YouTubeIcon from "@mui/icons-material/YouTube";
import {
	alpha,
	Avatar,
	Box,
	Button,
	Card,
	Chip,
	Divider,
	Grid,
	IconButton,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import moment from "moment";
import React from "react";
import { imgUrl } from "src/Helpers/utils/constants";
import type { IOfflineExhibitorDetails, ISocialMedia } from "../../type/offlineExhibitorType";

interface Props {
	exhibitor: IOfflineExhibitorDetails;
	totalScanned: number;
	onEdit: () => void;
	onDelete: () => void;
}

const getFullUrl = (path?: string) => {
	if (!path) return "";
	if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("blob:")) {
		return path;
	}
	return `${imgUrl}/${path}`;
};

const CompactExhibitorOverview: React.FC<Props> = ({
	exhibitor,
	totalScanned,
	onEdit,
	onDelete,
}) => {
	const theme = useTheme();

	// Parse categories safely
	let categories: string[] = [];
	if (Array.isArray(exhibitor.category)) {
		categories = exhibitor.category;
	} else if (typeof exhibitor.category === "string" && exhibitor.category.trim()) {
		try {
			const parsed = JSON.parse(exhibitor.category);
			if (Array.isArray(parsed)) categories = parsed;
			else if (typeof parsed === "string") categories = [parsed];
		} catch {
			categories = exhibitor.category
				.split(",")
				.map((c) => c.trim())
				.filter(Boolean);
		}
	}

	// Parse social media safely
	let socialMedia: ISocialMedia = {};
	if (exhibitor.social_media) {
		if (typeof exhibitor.social_media === "object") {
			socialMedia = exhibitor.social_media;
		} else if (typeof exhibitor.social_media === "string" && exhibitor.social_media.trim()) {
			try {
				socialMedia = JSON.parse(exhibitor.social_media);
			} catch {
				socialMedia = {};
			}
		}
	}

	const hasSocialMedia = Object.values(socialMedia).some(
		(link) => link && typeof link === "string" && link.trim() !== ""
	);

	const logoUrl = getFullUrl(exhibitor.logo);
	const brochureUrl = getFullUrl(exhibitor.brochure);

	return (
		<Card
			elevation={2}
			sx={{
				borderRadius: 2.5,
				overflow: "hidden",
				border: "1px solid",
				borderColor: "divider",
			}}
		>
			{/* Top Header Row */}
			<Box
				sx={{
					p: { xs: 2, sm: 2.5 },
					bgcolor: alpha(theme.palette.primary.main, 0.025),
					display: "flex",
					flexDirection: { xs: "column", md: "row" },
					alignItems: { xs: "flex-start", md: "center" },
					justifyContent: "space-between",
					gap: 2,
				}}
			>
				{/* Profile Logo & Name */}
				<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
					{logoUrl ? (
						<Avatar
							src={logoUrl}
							alt={exhibitor.name}
							variant="rounded"
							sx={{
								width: 50,
								height: 50,
								borderRadius: 2,
								bgcolor: "background.paper",
								border: "1.5px solid",
								borderColor: "divider",
								boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
							}}
						/>
					) : (
						<Avatar
							variant="rounded"
							sx={{
								width: 50,
								height: 50,
								borderRadius: 2,
								bgcolor: theme.palette.primary.main,
								color: "#ffffff",
								fontWeight: 700,
								fontSize: "1.3rem",
							}}
						>
							{exhibitor.name?.charAt(0)?.toUpperCase() || "E"}
						</Avatar>
					)}

					<Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
							<Typography variant="h3" fontWeight={700} color="text.primary">
								{exhibitor.name}
							</Typography>
							{exhibitor.exhibitor_no && (
								<Chip
									label={`No: ${exhibitor.exhibitor_no}`}
									color="primary"
									size="small"
									sx={{ fontWeight: 700, height: 22, fontSize: "0.75rem", borderRadius: 1 }}
								/>
							)}
						</Box>
					</Box>
				</Box>

				{/* Quick Stats & Action Buttons */}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1.5,
						flexWrap: "wrap",
						alignSelf: { xs: "stretch", md: "center" },
						justifyContent: { xs: "flex-start", md: "flex-end" },
					}}
				>
					{/* Metric Badges */}
					<Stack direction="row" spacing={1} sx={{ mr: { md: 1 } }}>
						<Tooltip title="Allocated Stalls" arrow>
							<Chip
								icon={<StorefrontTwoToneIcon sx={{ fontSize: 16 }} />}
								label={`Stalls: ${exhibitor.stalls?.length || 0}`}
								size="small"
								variant="outlined"
								sx={{ fontWeight: 600, borderRadius: 1.5 }}
							/>
						</Tooltip>
						<Tooltip title="Services Offered" arrow>
							<Chip
								icon={<MiscellaneousServicesTwoToneIcon sx={{ fontSize: 16 }} />}
								label={`Services: ${exhibitor.services?.length || 0}`}
								size="small"
								variant="outlined"
								sx={{ fontWeight: 600, borderRadius: 1.5 }}
							/>
						</Tooltip>
						<Tooltip title="Scanned Stalls Count" arrow>
							<Chip
								icon={<QrCodeScannerTwoToneIcon sx={{ fontSize: 16 }} />}
								label={`Scans: ${totalScanned}`}
								size="small"
								color="secondary"
								variant="outlined"
								sx={{ fontWeight: 600, borderRadius: 1.5 }}
							/>
						</Tooltip>
					</Stack>

					{/* Action Buttons */}
					{brochureUrl && (
						<Button
							variant="outlined"
							color="primary"
							size="small"
							component="a"
							href={brochureUrl}
							target="_blank"
							rel="noopener noreferrer"
							startIcon={<DescriptionTwoToneIcon fontSize="small" />}
							sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, py: 0.5 }}
						>
							Brochure
						</Button>
					)}

					<Button
						variant="contained"
						color="primary"
						size="small"
						startIcon={<EditTwoToneIcon fontSize="small" />}
						onClick={onEdit}
						sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, py: 0.5 }}
					>
						Edit
					</Button>

					<Button
						variant="outlined"
						color="error"
						size="small"
						startIcon={<DeleteTwoToneIcon fontSize="small" />}
						onClick={onDelete}
						sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, py: 0.5 }}
					>
						Delete
					</Button>
				</Box>
			</Box>

			{/* Dedicated Categories Strip - can grow dynamically */}
			{categories.length > 0 && (
				<Box
					sx={{
						px: { xs: 2, sm: 2.5 },
						py: 1.2,
						bgcolor: alpha(theme.palette.primary.main, 0.015),
						borderTop: "1px solid",
						borderBottom: "1px solid",
						borderColor: "divider",
						display: "flex",
						alignItems: "center",
						gap: 1.5,
						flexWrap: "wrap",
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
						<LocalOfferTwoToneIcon sx={{ fontSize: 16, color: "primary.main" }} />
						<Typography
							variant="caption"
							fontWeight={700}
							color="text.secondary"
							sx={{ textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap" }}
						>
							Categories ({categories.length}):
						</Typography>
					</Box>
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
						{categories.map((cat, idx) => (
							<Chip
								key={cat + idx}
								label={cat}
								size="small"
								sx={{
									height: 24,
									fontSize: "0.75rem",
									fontWeight: 600,
									borderRadius: 1.5,
									bgcolor: alpha(theme.palette.info.main, 0.08),
									color: theme.palette.info.dark,
									border: "1px solid",
									borderColor: alpha(theme.palette.info.main, 0.2),
								}}
							/>
						))}
					</Box>
				</Box>
			)}

			{/* Compact Modular Information Bar */}
			<Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5 }}>
				<Grid container spacing={2} alignItems="center">
					{/* Module A: Login / Account Email */}
					<Grid item xs={12} sm={6} md={3}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<EmailTwoToneIcon fontSize="small" color="primary" sx={{ opacity: 0.8 }} />
							<Box sx={{ overflow: "hidden" }}>
								<Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
									Account Email
								</Typography>
								<Typography variant="body2" fontWeight={600} noWrap color="text.primary">
									{exhibitor.email || "-"}
								</Typography>
							</Box>
						</Box>
					</Grid>

					{/* Module B: Company Email */}
					<Grid item xs={12} sm={6} md={3}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<EmailTwoToneIcon fontSize="small" color="action" sx={{ opacity: 0.8 }} />
							<Box sx={{ overflow: "hidden" }}>
								<Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
									Company Email
								</Typography>
								<Typography variant="body2" fontWeight={600} noWrap color="text.primary">
									{exhibitor.company_email || "-"}
								</Typography>
							</Box>
						</Box>
					</Grid>

					{/* Module C: Website & Registered */}
					<Grid item xs={12} sm={6} md={3}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<LanguageTwoToneIcon fontSize="small" color="info" sx={{ opacity: 0.8 }} />
							<Box sx={{ overflow: "hidden" }}>
								<Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
									Website
								</Typography>
								{exhibitor.website ? (
									<Typography
										variant="body2"
										fontWeight={600}
										component="a"
										href={
											exhibitor.website.startsWith("http")
												? exhibitor.website
												: `https://${exhibitor.website}`
										}
										target="_blank"
										rel="noopener noreferrer"
										color="primary.main"
										noWrap
										sx={{
											textDecoration: "none",
											"&:hover": { textDecoration: "underline" },
											display: "inline-flex",
											alignItems: "center",
											gap: 0.5,
										}}
									>
										{exhibitor.website.replace(/^https?:\/\//, "")}
										<OpenInNewIcon sx={{ fontSize: 13 }} />
									</Typography>
								) : (
									<Typography variant="body2" color="text.secondary">
										-
									</Typography>
								)}
							</Box>
						</Box>
					</Grid>

					{/* Module D: Social Links & Reg Date */}
					<Grid item xs={12} sm={6} md={3}>
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: { md: "flex-end" }, gap: 1 }}>
							{hasSocialMedia ? (
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
									{socialMedia.facebook && (
										<Tooltip title="Facebook" arrow>
											<IconButton
												size="small"
												component="a"
												href={socialMedia.facebook}
												target="_blank"
												rel="noopener noreferrer"
												sx={{ p: 0.5, color: "#1877F2" }}
											>
												<FacebookIcon sx={{ fontSize: 18 }} />
											</IconButton>
										</Tooltip>
									)}
									{socialMedia.linkedin && (
										<Tooltip title="LinkedIn" arrow>
											<IconButton
												size="small"
												component="a"
												href={socialMedia.linkedin}
												target="_blank"
												rel="noopener noreferrer"
												sx={{ p: 0.5, color: "#0A66C2" }}
											>
												<LinkedInIcon sx={{ fontSize: 18 }} />
											</IconButton>
										</Tooltip>
									)}
									{socialMedia.twitter && (
										<Tooltip title="Twitter / X" arrow>
											<IconButton
												size="small"
												component="a"
												href={socialMedia.twitter}
												target="_blank"
												rel="noopener noreferrer"
												sx={{ p: 0.5, color: "#1DA1F2" }}
											>
												<TwitterIcon sx={{ fontSize: 18 }} />
											</IconButton>
										</Tooltip>
									)}
									{socialMedia.instagram && (
										<Tooltip title="Instagram" arrow>
											<IconButton
												size="small"
												component="a"
												href={socialMedia.instagram}
												target="_blank"
												rel="noopener noreferrer"
												sx={{ p: 0.5, color: "#E1306C" }}
											>
												<InstagramIcon sx={{ fontSize: 18 }} />
											</IconButton>
										</Tooltip>
									)}
									{socialMedia.youtube && (
										<Tooltip title="YouTube" arrow>
											<IconButton
												size="small"
												component="a"
												href={socialMedia.youtube}
												target="_blank"
												rel="noopener noreferrer"
												sx={{ p: 0.5, color: "#FF0000" }}
											>
												<YouTubeIcon sx={{ fontSize: 18 }} />
											</IconButton>
										</Tooltip>
									)}
								</Box>
							) : null}

							{/* Registration Date Badge */}
							<Tooltip
								title={`Registered: ${
									exhibitor.created_at ? moment(exhibitor.created_at).format("LL") : "N/A"
								}`}
								arrow
							>
								<Chip
									icon={<CalendarTodayTwoToneIcon sx={{ fontSize: 13 }} />}
									label={
										exhibitor.created_at
											? moment(exhibitor.created_at).format("DD MMM YYYY")
											: "N/A"
									}
									size="small"
									sx={{
										height: 22,
										fontSize: "0.72rem",
										bgcolor: "transparent",
										border: "1px solid",
										borderColor: "divider",
									}}
								/>
							</Tooltip>
						</Box>
					</Grid>
				</Grid>
			</Box>
		</Card>
	);
};

export default CompactExhibitorOverview;
